// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PlayerStatistics, StatisticsGameDefinition } from './types';

type Options = {
  apiBaseUrl: string;
  token: string | null | undefined;
  /** null = all time. */
  year: number | null;
};

/**
 * What went wrong, not how to say it. The caller translates, so the message follows a language
 * switch and — more importantly — the fetch effect does not depend on a `t` that is a new
 * function on every render, which would make it refetch forever.
 */
export type StatisticsErrorCode = 'noPlayer' | 'loadFailed';

type Result = {
  statistics: PlayerStatistics | null;
  gameDefinitions: StatisticsGameDefinition[];
  loading: boolean;
  error: StatisticsErrorCode | null;
  reload: () => void;
};

/**
 * Loads the statistics payload plus the game definitions the games list needs for display names.
 * The definitions never change within a session, so they are fetched once and kept across
 * period switches — only the statistics call repeats.
 */
export function usePlayerStatistics({ apiBaseUrl, token, year }: Options): Result {
  const [statistics, setStatistics] = useState<PlayerStatistics | null>(null);
  const [gameDefinitions, setGameDefinitions] = useState<StatisticsGameDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<StatisticsErrorCode | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const definitionsLoadedRef = useRef(false);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined),
    [token]
  );

  useEffect(() => {
    if (!token) {
      setStatistics(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const query = year === null ? '' : `?year=${year}`;
    fetch(`${apiBaseUrl}/api/player/statistics${query}`, { headers, signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(String(response.status));
        }
        return (await response.json()) as PlayerStatistics;
      })
      .then((payload) => {
        if (controller.signal.aborted) return;
        setStatistics(payload);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setStatistics(null);
        setError(err instanceof Error && err.message === '404' ? 'noPlayer' : 'loadFailed');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [apiBaseUrl, headers, reloadToken, token, year]);

  useEffect(() => {
    if (!token || definitionsLoadedRef.current) {
      return;
    }

    const controller = new AbortController();
    fetch(`${apiBaseUrl}/api/game-definitions`, { headers, signal: controller.signal })
      .then(async (response) => (response.ok ? ((await response.json()) as StatisticsGameDefinition[]) : []))
      .then((payload) => {
        if (controller.signal.aborted) return;
        definitionsLoadedRef.current = true;
        setGameDefinitions(Array.isArray(payload) ? payload : []);
      })
      // Names are a nicety — without them the games list falls back to a generic label rather
      // than failing the whole screen.
      .catch(() => undefined);

    return () => controller.abort();
  }, [apiBaseUrl, headers, token]);

  return { statistics, gameDefinitions, loading, error, reload };
}
